import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm } from 'redux-form';

import * as store from 'store';

import { RANDOMIZATION_HYPOTHESIS_TESTING_FORM } from 'store/form';
import { getFilteredParametersValuesNotEmptyLength } from 'store/dataset';

import SettingsFormComponent from './Component';

import {
  getPrimaryParameterName,
  getSecondaryParameterName
} from 'store/form/randomizationHypothesisTesting/selectors';

class SettingsFormContainer extends React.PureComponent {
  constructor (props) {
    super(props);

    this.handleGenerateSamplesClick = () =>
      this.props.bootstrapSamples();

    this.handleResetClick = () =>
      this.props.resetRandomizationHypothesisTesting();

    this.handlePrimaryParameterChange = parameterName =>
      // Resets test selection dropdown option
      this.props.setTestSelectionType(parameterName);

    this.handleSecondaryParameterChange = secondaryParameter => {
      this.props.change('testSelection', secondaryParameter ? 'slope' : 'mean');
      this.props.change('nullValue', secondaryParameter ? '0' : '');
    };
  }

  componentDidMount () {
    if (this.props.samplesExist) {
      return;
    }

    this.props.initialize({
      primaryParameter: '',
      secondaryParameter: '',
      primaryParameterLevel: '',

      alpha: 0.05,

      numberOfSamples: 100,

      tailType: 'left',

      cutoffLeft: '',
      cutoffRight: ''
    });
  }

  get optionsControlsVisible () {
    const {
      primaryParameterType,
      primaryParameterSelected,
      primaryParameterLevelSelected
    } = this.props;

    if (!primaryParameterSelected) {
      return false;
    }

    const isNumerical = primaryParameterType === 'numerical';
    const isCategorical = primaryParameterType === 'categorical';

    return isNumerical || (isCategorical && primaryParameterLevelSelected);
  }

  get showEmptyDatasetWarning () {
    return this.props.datasetLength <= 0;
  }

  get generateSamplesButtonDisabled () {
    return this.props.invalid || this.showEmptyDatasetWarning;
  }

  render () {
    return (
      <SettingsFormComponent
        form={this.props.form}
        onResetClick={this.handleResetClick}
        samplesExist={this.props.samplesExist}
        optionsControlsVisible={this.optionsControlsVisible}
        primaryParameterName={this.props.primaryParameterName}
        showEmptyDatasetWarning={this.showEmptyDatasetWarning}
        onGenerateSamplesClick={this.handleGenerateSamplesClick}
        primaryParameterLevels={this.props.primaryParameterLevels}
        onPrimaryParameterChange={this.handlePrimaryParameterChange}
        onSecondaryParameterChange={this.handleSecondaryParameterChange}
        generateSamplesButtonDisabled={this.generateSamplesButtonDisabled}
        secondaryParameterDropdownVisible={this.props.numericalPrimaryParameter}
      />
    );
  }
}

SettingsFormContainer.propTypes = {
  form: PropTypes.string.isRequired,

  primaryParameterName: PropTypes.string,
  primaryParameterType: PropTypes.string,
  primaryParameterLevels: PropTypes.array,

  samplesExist: PropTypes.bool.isRequired,
  numericalPrimaryParameter: PropTypes.bool.isRequired,

  primaryParameterSelected: PropTypes.bool.isRequired,
  primaryParameterLevelSelected: PropTypes.bool.isRequired,

  change: PropTypes.func.isRequired,
  invalid: PropTypes.bool.isRequired,
  initialize: PropTypes.func.isRequired,

  bootstrapSamples: PropTypes.func.isRequired,
  setTestSelectionType: PropTypes.func.isRequired,
  resetRandomizationHypothesisTesting: PropTypes.func.isRequired,

  datasetLength: PropTypes.number
};

SettingsFormContainer.defaultProps = {
  primaryParameterName: null,
  primaryParameterType: null,
  primaryParameterLevels: null,
  datasetLength: null
};

const {
  form: {
    randomizationHypothesisTesting: {
      isPrimaryParameterLevelSelected,
      isPrimaryParameterNumerical,
      isPrimaryParameterSelected,

      getPrimaryParameterType,
      getPrimaryParameterLevels
    }
  },

  scenes: {
    randomizationHypothesisTesting: {
      samples: {
        doSamplesExist
      },

      bootstrapSamples,
      setTestSelectionType,
      resetRandomizationHypothesisTesting
    }
  }
} = store;

const mapStateToProps = state => {
  const primaryParameterName = getPrimaryParameterName(state);
  const secondaryParameterName = getSecondaryParameterName(state);

  return ({
    datasetLength: getFilteredParametersValuesNotEmptyLength(state, primaryParameterName, secondaryParameterName),

    primaryParameterSelected: isPrimaryParameterSelected(state),
    numericalPrimaryParameter: isPrimaryParameterNumerical(state),
    primaryParameterLevelSelected: isPrimaryParameterLevelSelected(state),

    primaryParameterName,
    primaryParameterType: getPrimaryParameterType(state),
    primaryParameterLevels: getPrimaryParameterLevels(state),

    samplesExist: doSamplesExist(state)
  });
};

const mapDispatchToProps = {
  resetRandomizationHypothesisTesting,
  setTestSelectionType,
  bootstrapSamples
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({ form: RANDOMIZATION_HYPOTHESIS_TESTING_FORM, destroyOnUnmount: false })(
    SettingsFormContainer
  )
);
